import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import { FaDownload, FaCheckCircle, FaExclamationCircle, FaHistory, FaExternalLinkAlt } from "react-icons/fa";
import { allorders } from "../Redux/Slice/Payment.slice";
import { BASE_URL } from "../Utils/baseUrl";

const Downloads = () => {
  const dispatch = useDispatch();
  const allOrders = useSelector((state) => state.payment.orders);
  const [downloadItems, setDownloadItems] = useState([]);

  useEffect(() => {
    dispatch(allorders());
  }, [dispatch]);

  useEffect(() => {
    document.title = "Downloads • YOYO";
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (allOrders && allOrders.length > 0) {
      // Extract all paid items from orders
      const items = allOrders
        .filter((order) => order.status === "paid")
        .flatMap((order) =>
          order.items.map((item) => ({
            ...item,
            orderDate: order.createdAt,
            orderId: order._id,
            orderNumber: order?.orderNumber || order._id.toString().slice(0, 8).toUpperCase(),
          }))
        )
        .sort((a, b) => new Date(b.orderDate) - new Date(a.orderDate));
      setDownloadItems(items);
    }
  }, [allOrders]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleDownload = (token) => {
    if (!token) return;
    // Open download link in new tab
    const downloadUrl = `${BASE_URL}/download/${token}`;
    window.open(downloadUrl, "_blank");

    // Refresh orders after a short delay to update token status
    setTimeout(() => {
      dispatch(allorders());
    }, 2000);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 },
  };

  return (
    <div className="space-y-4 sm:space-y-6 inter-font">
      {/* Info Card (Moved to Top) */}
      <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 flex flex-col sm:flex-row gap-4 backdrop-blur-sm">
        <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center flex-shrink-0 text-purple-400">
          <FaExclamationCircle />
        </div>
        <div>
          <h5 className="text-white font-bold text-sm">Download Policy</h5>
          <p className="text-gray-400 text-[11px] sm:text-xs mt-1 leading-relaxed">
            For security reasons, each unique download link is valid for a single successful download attempt.
            Once the link is accessed and the file starts downloading, the token is marked as "Used".
            If you encounter issues during download, please contact our 24/7 support team.
          </p>
        </div>
      </div>

      <div className="bg-[#1414145d] backdrop-blur-md border-[2px] border-white/15 rounded-lg p-4 sm:p-6">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 sm:mb-8 gap-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-3">
              <FaHistory className="text-purple-400" /> Download Manager
            </h2>
            <p className="text-gray-400 text-[11px] sm:text-sm mt-1 leading-tight">Manage your purchased games and download history</p>
          </div>
          <div className="bg-purple-500/10 border border-purple-500/20 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full flex-shrink-0">
            <span className="text-purple-400 font-bold text-xs sm:text-sm whitespace-nowrap">{downloadItems.length} Games Owned</span>
          </div>
        </div>

        <div className="max-h-[50vh] sm:max-h-[60vh] overflow-y-auto pr-1 sm:pr-2 custom-scrollbar">
          {downloadItems.length === 0 ? (
            <div className="py-16 sm:py-20 text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaDownload className="text-gray-600 text-2xl sm:text-3xl" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-white">No Downloads Found</h3>
              <p className="text-gray-400 text-xs sm:text-sm mt-2">You haven't purchased any games yet or your orders are pending.</p>
            </div>
          ) : (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-1 gap-3 sm:gap-4 pb-4"
            >
              {downloadItems.map((item, index) => (
                <motion.div
                  key={`${item.orderId}-${index}`}
                  variants={itemVariants}
                  className="group relative bg-white/5 border border-white/10 rounded-xl sm:rounded-2xl p-4 sm:p-6 hover:bg-white/[0.08] transition-all duration-300"
                >
                  <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6">
                    {/* Game Icon/Cover */}
                    <div className="relative w-20 h-20 md:w-20 md:h-20 flex-shrink-0">
                      <img
                        src={item.game?.cover_image?.url}
                        alt={item.game?.title}
                        className="w-full h-full object-cover rounded-xl border border-white/10 shadow-lg group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute -top-2 -right-2 bg-gradient-to-r from-purple-600 to-pink-600 px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider text-white shadow-lg shadow-purple-900/20">
                        {item.platform}
                      </div>
                    </div>

                    {/* Info */}
                    <div className="flex-1 text-center md:text-left min-w-0 w-full">
                      <h4 className="text-base md:text-lg font-bold text-white group-hover:text-purple-400 transition-colors truncate">
                        {item.game?.title}
                      </h4>
                      <div className="flex flex-wrap items-center justify-center md:justify-start gap-x-4 gap-y-1 mt-2 text-[10px] sm:text-xs text-gray-400">
                        <span className="flex items-center gap-1.5">
                          <span className="w-1 h-1 bg-gray-500 rounded-full"></span>
                          Purchased: {formatDate(item.orderDate)}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <span className="w-1 h-1 bg-gray-500 rounded-full"></span>
                          Order: {item.orderNumber ? item.orderNumber : `#${item.orderId.slice(-8).toUpperCase()}`}
                        </span>
                      </div>
                    </div>

                    {/* Status & Action */}
                    <div className="flex flex-col items-center md:items-end gap-2.5 md:gap-3 w-full md:w-auto">
                      <div className="flex items-center gap-2">
                        {item.downloadTokenUsed ? (
                          <span className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-widest text-red-400 bg-red-400/10 px-3 py-1 rounded-full border border-red-400/20">
                            <FaExclamationCircle /> Link Expired
                          </span>
                        ) : (
                          <span className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-widest text-emerald-400 bg-emerald-400/10 px-3 py-1 rounded-full border border-emerald-400/20">
                            <FaCheckCircle /> Available
                          </span>
                        )}
                      </div>

                      <button
                        onClick={() => handleDownload(item.downloadToken)}
                        disabled={item.downloadTokenUsed}
                        className={`w-full md:w-auto flex items-center justify-center gap-2 px-6 py-2 md:py-2.5 rounded-lg md:rounded-xl text-[10px] sm:text-sm font-bold transition-all duration-300 ${item.downloadTokenUsed
                          ? 'bg-white/5 text-gray-500 cursor-not-allowed border border-white/5'
                          : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg shadow-purple-900/20 hover:shadow-purple-900/40 hover:-translate-y-0.5 active:translate-y-0'
                          }`}
                      >
                        <FaDownload className={item.downloadTokenUsed ? '' : 'animate-bounce'} />
                        {item.downloadTokenUsed ? 'Used Once' : 'Download Now'}
                      </button>

                      {item.downloadTokenUsed && (
                        <p className="text-[10px] text-gray-500 uppercase tracking-wide">Contact support for re-download</p>
                      )}
                    </div>
                  </div>

                  {/* Subtle Progress/History Bar (Decorative) */}
                  <div className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-transparent via-purple-500/20 to-transparent w-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Downloads;
